// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Libs
import {
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable
} from '@tanstack/react-table';
// Components
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '../../ui/table';
import TablePagination from './pagination/TablePagination';
import TableFilters from './filter/TableFilters';
import TableBodySkeleton from './skeleton/TableBodySkeleton';
import CustomPagination from './pagination/CustomPagination';
import ModalAskConfirmation from '../modal/components/modals/ModalAskConfirmation';
import ModalExport from './export/ModalExport';
import Entity from '../entity/Entity';
import { Button } from '../../ui/button';
// Common
import getInitialValueForRowSelected from './utils/GetInitialValueForRowSelected';
import ICONS from '@/common-utils/enums/icons';
import TABLE_CONSTANTS from './enums/Constants';
import useTableTimeout from '@/common-utils/hooks/useTableTimeout';
// Style
import './DataTable.scss';

/**
 *
 * @param root0
 * @param root0.data
 * @param root0.columns
 * @param root0.columnsId
 * @param root0.dataFetchingIsLoading
 * @param root0.initialQuery
 * @param root0.query
 * @param root0.setQuery
 * @param root0.setLaunchRefetch
 * @param root0.selectionMode
 * @param root0.singleSelection
 * @param root0.rowsSelected
 * @param root0.setRowsSelected
 * @param root0.pageActions
 * @param root0.exportProps
 * @param root0.importProps
 * @param root0.hasPartialResults
 * @param root0.queryStored
 * @param root0.storeQuery
 * @param root0.filtersEventName
 * @param root0.error
 * @param root0.refetch
 * @returns {JSX} DataTable
 */
function DataTable({
    data,
    columns,
    columnsId,
    dataFetchingIsLoading,
    initialQuery,
    query,
    setQuery,
    queryStored,
    storeQuery,
    setLaunchRefetch,
    selectionMode,
    singleSelection = false,
    rowsSelected,
    setRowsSelected,
    pageActions = undefined,
    exportProps,
    importProps,
    hasPartialResults = false,
    filtersEventName = undefined,
    error = null,
    refetch = undefined
}) {
    const PAGE_SIZE = 100;

    // Table Functions
    const getColumnsOrderToDisplay = () => {
        const columnsFromLocalStorage = JSON.parse(localStorage.getItem(`${columnsId}-order`));

        // If no columns from local storage, return the column ids from the columns prop
        if (!columnsFromLocalStorage) {
            return columns.map(c => c?.id).filter(Boolean);
        }

        // If selection mode is enabled return the columns from local storage with the selector column id first
        // Otherwise, return the columns from local storage
        return selectionMode ? [`${columnsId}-selector`, ...columnsFromLocalStorage] : columnsFromLocalStorage;
    };
    const getColumnsToHide = () => {
        const columnsFromLocalStorage = JSON.parse(localStorage.getItem(columnsId));
        return columnsFromLocalStorage || {};
    };

    // Table State
    const [sorting, setSorting] = useState([]);
    const [columnFilters, setColumnFilters] = useState([]);
    const [columnVisibility, setColumnVisibility] = useState(getColumnsToHide());
    const [columnOrder, setColumnOrder] = useState(getColumnsOrderToDisplay());
    const [rowSelection, setRowSelection] = useState(
        selectionMode && rowsSelected && Object.keys(rowsSelected.rowIds).length !== 0
            ? rowsSelected.rowIds
            : {}
    );
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: PAGE_SIZE
    });
    const [resetPagination, setResetPagination] = useState(false);

    // Timeout State
    const [showModalConfirmExportAfterTimeout, setShowModalConfirmExportAfterTimeout] = useState(false);
    const [showExportOnTimeout, setShowExportOnTimeout] = useState(false);

    // Table
    const table = useReactTable({
        data: data && data.items && !error ? data.items : [],
        columns,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onColumnOrderChange: setColumnOrder,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        onRowSelectionChange: setRowSelection,
        // getPaginationRowModel: getPaginationRowModel(), //not needed for server-side pagination
        manualPagination: true, //turn off client-side pagination
        rowCount: data?.totalResults ?? data?.totalPartialResults ?? 0,
        onPaginationChange: setPagination,
        getRowId: row => row.uuid,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            columnOrder,
            rowSelection,
            pagination
        },
        // DEBUG PURPOSE ONLY - SET TO TRUE TO HAVE LOGS
        debugTable: false,
        debugHeaders: false,
        debugColumns: false
    });

    const [currentPageCount, setCurrentPageCount] = useState({ rowCount: 0, pageCount: 0 });

    // Hooks
    useEffect(() => {
        if (data?.totalResults !== undefined && data.totalResults !== currentPageCount.pageCount) {
            setCurrentPageCount({
                rowCount: data.totalResults,
                pageCount: Math.ceil(data.totalResults / PAGE_SIZE) || 1
            });
        }
        else if (data?.totalPartialResults !== undefined) {
            const offsetRows = query.offset ?? 0;

            setCurrentPageCount({
                rowCount: data.totalPartialResults,
                pageCount: Math.ceil((offsetRows + data.totalPartialResults) / PAGE_SIZE) || 1
            });

            // Set the scroll to the top of the table body
            document.getElementById('table-body').scrollTo({ top: 0 });
        }
    }, [data]);

    useEffect(() => {
        // On selection mode
        if (selectionMode) {
            let copy = [];
            if (Object.keys(rowSelection).length) {
                // Multiple selection
                if (!singleSelection && Object.keys(rowSelection).length !== Object.keys(rowsSelected.rowIds).length) {
                    // Find number of diff between rowSelection / rowsSelected
                    const diff = Object.keys(rowSelection).length - Object.keys(rowsSelected.rowIds).length;
                    // Make a copy of the rows data before updating it (removes dependancies with the state)
                    copy = [...rowsSelected.rowsData];

                    // If there are more selected elements
                    if (diff >= 1) {
                        // Retrieves rows data for the new selected elements
                        const rowsDataUuid = Object.keys(rowSelection)
                            .filter(objectOne => !Object.keys(rowsSelected.rowIds).some(objectTwo => objectOne === objectTwo));
                        // For each, adds it to our copy
                        rowsDataUuid.forEach(dataUuidToAdd => {
                            copy.push(data.items.find(apiData => apiData.uuid === dataUuidToAdd));
                        });
                    }
                    // If there are less selected elements
                    if (diff <= -1) {
                        // Retrieves deselected rows ids
                        const rowsDataNotSav = Object.keys(rowsSelected.rowIds)
                            .filter(objectOne => !Object.keys(rowSelection).some(objectTwo => objectOne === objectTwo));
                        // For each, removes it from the copy
                        rowsDataNotSav.forEach(dataUuidToDel => {
                            copy = copy.filter(apiData => apiData.uuid !== dataUuidToDel);
                        });
                    }
                }
                // Single selection
                if (singleSelection) {
                    // Remove deselected rows from rowsSelected
                    copy = rowsSelected.rowsData.filter(
                        (row) => rowSelection[row.uuid]
                    );

                    // Add new selected row to rowsSelected
                    Object.keys(rowSelection).forEach((rowId) => {
                        if (!rowsSelected.rowIds[rowId]) {
                            const newRow = data.items.find((item) => item.uuid === rowId);
                            if (newRow) {
                                copy.push(newRow);
                            }
                        }
                    });
                }
                // Updates state based on the updated copy of rows selected
                if (copy.length) {
                    setRowsSelected({
                        rowsData: copy,
                        rowIds: rowSelection
                    });
                }
            }
            else {
                setRowsSelected({
                    rowsData: [],
                    rowIds: {}
                });
            }
        }
    }, [rowSelection]);
    // Timeout Hook
    const isRetrying = useTableTimeout(
        error,
        refetch,
        dataFetchingIsLoading,
        setShowModalConfirmExportAfterTimeout,
        !!exportProps
    );

    // Other functions
    const storeColumnsOrderToDisplay = (newColumnOrder) => {
        try {
            localStorage.setItem(`${columnsId}-order`, JSON.stringify(newColumnOrder));
        }
        catch {
            return null;
        }
    };

    const storeColumnsToHide = () => {
        try {
            localStorage.setItem(columnsId, JSON.stringify(columnVisibility));
        }
        catch {
            return null;
        }
    };
    const handleColumnOrderChange = (newColumnOrder) => {
        setColumnOrder(newColumnOrder);
        storeColumnsOrderToDisplay(newColumnOrder);
        storeColumnsToHide();
    };

    const pageUpdate = (direction, offset = null) => {
        let newOffset;
        if (offset === null || offset === undefined) {
            newOffset = direction ? query.offset + query.limit : query.offset - query.limit;
        } else {
            newOffset = offset;
        }
        setQuery({
            offset: newOffset,
            limit: TABLE_CONSTANTS.ROWS_NUMBER_PER_PAGE,
            orders: query.orders,
            filters: query.filters
        });

        setLaunchRefetch(true);
    };

    return (
        <div>
            <div className='flex flex-col'>
                <div className='flex-1'>
                    {
                        query &&
                        <TableFilters
                            table={table}
                            setResetPagination={setResetPagination}
                            columns={columns}
                            initialQuery={initialQuery}
                            query={query}
                            setQuery={setQuery}
                            queryStored={queryStored}
                            storeQuery={storeQuery}
                            setLaunchRefetch={setLaunchRefetch}
                            columnOrder={columnOrder}
                            handleColumnOrderChange={handleColumnOrderChange}
                            selectionMode={selectionMode}
                            pageActions={pageActions}
                            exportProps={exportProps}
                            importProps={importProps}
                            filtersEventName={filtersEventName}
                        />
                    }
                </div>
            </div>
            {
                selectionMode && !singleSelection &&
                <Button
                    onClick={() => {
                        setRowsSelected(getInitialValueForRowSelected());
                        setRowSelection({});
                    }}
                    icon={ICONS.DESELECT}
                    entity='table.deselect_all'
                    className={'ml-4 mb-4'}
                    disabled={rowsSelected?.rowsData.length === 0}
                />
            }
            <div id='table-scroll-container' className='overflow-auto'>
                <div id='table-body' className='overflow-auto'>
                    <Table className={'table bg-transparent'}>
                        <TableHeader>
                            {table.getHeaderGroups().map((headerGroup, index) => (
                                <TableRow key={`${headerGroup.id}-${index}`}>
                                    {headerGroup.headers.map((header, indx) => {
                                        return (
                                            <TableHead key={`${header.id}-${indx}`}>
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                            </TableHead>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableHeader>


                        <TableBody className='bg-white'>
                            {dataFetchingIsLoading || isRetrying ? (
                                <TableBodySkeleton columns={table.getVisibleLeafColumns().length} rows={PAGE_SIZE} />
                            ) :
                                table.getRowModel().rows?.length ? (
                                    table.getRowModel().rows.map((row, index) => (
                                        <TableRow
                                            key={`${row.id}-${index}`}
                                            data-state={row.getIsSelected() && 'selected'}
                                            className={row.class}
                                        >
                                            {row.getVisibleCells().map((cell, indx) => (
                                                <TableCell
                                                    key={`${cell.id}-${indx}`}
                                                    className={cell.column.columnDef.entity ? 'px-4 py-1' : 'px-1'}
                                                >
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} className='h-24 p-0'>
                                            <div
                                                className='sticky left-0 flex h-24 items-center justify-center'
                                                style={{
                                                    width: document.getElementById('table-scroll-container')?.clientWidth
                                                }}
                                            >
                                                <Entity entity='table.no_result' />
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )}
                        </TableBody>

                    </Table>
                </div>
            </div>
            {
                query && !hasPartialResults &&
                (
                    <TablePagination
                        table={table}
                        pagination={table.options.state.pagination}
                        pageUpdate={pageUpdate}
                        dataFetchingIsLoading={dataFetchingIsLoading}
                        resetPagination={resetPagination}
                        setResetPagination={setResetPagination}

                        columnOrder={columnOrder}
                        handleColumnOrderChange={handleColumnOrderChange}
                        currentPageCount={currentPageCount}
                        selectionMode={selectionMode}
                    />
                )
            }
            {
                query && hasPartialResults &&
                (
                    <CustomPagination
                        table={table}
                        query={query}
                        pagination={table.options.state.pagination}
                        pageUpdate={pageUpdate}
                        dataFetchingIsLoading={dataFetchingIsLoading}
                        resetPagination={resetPagination}
                        setResetPagination={setResetPagination}

                        columnOrder={columnOrder}
                        handleColumnOrderChange={handleColumnOrderChange}
                        currentPageCount={currentPageCount}
                        selectionMode={selectionMode}
                    />
                )
            }
            {
                showModalConfirmExportAfterTimeout &&
                <ModalAskConfirmation
                    entityTitle='modal.export.on_timeout.confirmation_title'
                    entityBody='modal.export.on_timeout.confirmation_body'
                    confirmChoice={() => {
                        setShowModalConfirmExportAfterTimeout(false);
                        setShowExportOnTimeout(true);
                    }}
                    isLoading={false}
                    isOpen={showModalConfirmExportAfterTimeout}
                    setIsOpen={setShowModalConfirmExportAfterTimeout}
                />
            }
            {
                showExportOnTimeout &&
                <ModalExport
                    isOpen={showExportOnTimeout}
                    setIsOpen={setShowExportOnTimeout}
                    exportApi={exportProps?.api}
                    uuidToExport={exportProps?.uuid}
                    queryFilters={query?.filters}
                    customExportQuery={exportProps?.query}
                    origin='TIMEOUT'
                />
            }
        </div>

    );
}

DataTable.propTypes = {
    data: PropTypes.object,
    columns: PropTypes.array.isRequired,
    columnsId: PropTypes.string.isRequired,
    dataFetchingIsLoading: PropTypes.bool,
    query: PropTypes.object,
    initialQuery: PropTypes.object,
    setQuery: PropTypes.func,
    queryStored: PropTypes.object,
    storeQuery: PropTypes.func,
    setLaunchRefetch: PropTypes.func,
    selectionMode: PropTypes.bool,
    singleSelection: PropTypes.bool,
    rowsSelected: PropTypes.object,
    setRowsSelected: PropTypes.func,
    pageActions: PropTypes.node,
    exportProps: PropTypes.shape({
        permissions: PropTypes.array,
        api: PropTypes.object,
        uuid: PropTypes.string,
        query: PropTypes.object
    }),
    importProps: PropTypes.object,
    hasPartialResults: PropTypes.bool,
    filtersEventName: PropTypes.string,
    error: PropTypes.object,
    refetch: PropTypes.func
};

export default DataTable;